'use client';

import { c as _c } from "react/compiler-runtime";
import { Tooltip as TooltipPrimitive } from 'radix-ui';
import type * as React from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { Tooltip, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/ui/utils';

// Re-export compound primitives — see app-dialog.tsx for migration rationale.
export { Tooltip, TooltipProvider, TooltipTrigger };

// Morfi tokens layered over stock tooltip.tsx (which uses z-50 / rounded-md).
// TooltipContent implementation lives here (FQ-111) so the stock primitive stays
// byte-identical to `.shadcn-stock/tooltip.tsx`. Optional `showArrow` / `arrowClassName`
// support outcome-matched caret tinting (`globals.css` `--tooltip-caret-fill`).
const TOOLTIP_CONTENT_MORFI_TOKENS = 'z-popover rounded-overlay will-change-transform' as const;
const TOOLTIP_ARROW_BASE = 'z-popover size-2.5 translate-y-[calc(-50%_-_2px)] rotate-45 rounded-[2px]' as const;
type RadixTooltipContentProps = React.ComponentProps<typeof TooltipPrimitive.Content>;
export interface TooltipContentProps extends RadixTooltipContentProps {
  /**
   * When false, omit the caret — callers that restyle Content (e.g. outcome
   * chrome) otherwise get a mismatched default arrow tint.
   * @default true
   */
  showArrow?: boolean | undefined;
  /**
   * Merged onto `TooltipPrimitive.Arrow` after layout/position bases.
   * When omitted, arrow uses inverted tooltip chrome (`foreground-primary`).
   */
  arrowClassName?: string | undefined;
}
export function TooltipContent(t0) {
  const $ = _c(18);
  let arrowClassName;
  let children;
  let className;
  let rest;
  let t1;
  let t2;
  if ($[0] !== t0) {
    ({
      className,
      sideOffset: t1,
      children,
      showArrow: t2,
      arrowClassName,
      ...rest
    } = t0);
    $[0] = t0;
    $[1] = arrowClassName;
    $[2] = children;
    $[3] = className;
    $[4] = rest;
    $[5] = t1;
    $[6] = t2;
  } else {
    arrowClassName = $[1];
    children = $[2];
    className = $[3];
    rest = $[4];
    t1 = $[5];
    t2 = $[6];
  }
  const sideOffset = t1 === undefined ? 0 : t1;
  const showArrow = t2 === undefined ? true : t2;
  let t3;
  if ($[7] !== className) {
    t3 = cn("w-fit origin-(--radix-tooltip-content-transform-origin) animate-in rounded-md bg-foreground-primary px-3 py-1.5 text-xs text-balance text-background-primary fade-in-0 zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95", TOOLTIP_CONTENT_MORFI_TOKENS, className);
    $[7] = className;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== arrowClassName || $[10] !== showArrow) {
    t4 = showArrow ? <TooltipPrimitive.Arrow className={cn(TOOLTIP_ARROW_BASE, arrowClassName ?? "bg-foreground-primary fill-foreground")} /> : null;
    $[9] = arrowClassName;
    $[10] = showArrow;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== children || $[13] !== rest || $[14] !== sideOffset || $[15] !== t3 || $[16] !== t4) {
    t5 = <TooltipPrimitive.Portal><TooltipPrimitive.Content data-slot="tooltip-content" sideOffset={sideOffset} className={t3} {...rest}>{children}{t4}</TooltipPrimitive.Content></TooltipPrimitive.Portal>;
    $[12] = children;
    $[13] = rest;
    $[14] = sideOffset;
    $[15] = t3;
    $[16] = t4;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  return t5;
}

// ---------------------------------------------------------------------------
// AppTooltip — flat API
// ---------------------------------------------------------------------------

export type AppTooltipSide = 'top' | 'right' | 'bottom' | 'left';
export type AppTooltipAlign = 'start' | 'center' | 'end';
export interface AppTooltipProps {
  /** Tooltip content. When `undefined` or `null`, the children render unwrapped. */
  content: ReactNode;
  /** Trigger element — wrapped in `<TooltipTrigger asChild>`. */
  children: ReactNode;
  side?: AppTooltipSide | undefined;
  align?: AppTooltipAlign | undefined;
  sideOffset?: number | undefined;
  delayDuration?: number | undefined;
  /** When true, render `children` unwrapped — no tooltip wiring. */
  disabled?: boolean | undefined;
  /** Force the tooltip open on mount (mostly for tests / showcase). */
  defaultOpen?: boolean | undefined;
  /** Controlled open state. */
  open?: boolean | undefined;
  onOpenChange?: ((open: boolean) => void) | undefined;
  contentClassName?: string | undefined;
  /** Merged onto Radix `Tooltip.Content` (e.g. `--tooltip-caret-fill` for SVG polygon). */
  contentStyle?: CSSProperties | undefined;
  arrowClassName?: string | undefined;
  /** @default true — set false to hide the caret entirely. */
  showArrow?: boolean | undefined;
}
interface AppTooltipComponent {
  (props: AppTooltipProps): React.ReactNode;
  Provider: typeof TooltipProvider;
}
function AppTooltipImpl(t0) {
  const $ = _c(44);
  const {
    content,
    children,
    side,
    align,
    sideOffset,
    delayDuration,
    disabled,
    defaultOpen,
    open,
    onOpenChange,
    contentClassName,
    contentStyle,
    arrowClassName,
    showArrow
  } = t0;
  const hasContent = content !== undefined && content !== null && content !== false;
  if (disabled === true || !hasContent) {
    let t1;
    if ($[0] !== children) {
      t1 = <>{children}</>;
      $[0] = children;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  let t1;
  if ($[2] !== delayDuration) {
    t1 = delayDuration !== undefined ? {
      delayDuration
    } : {};
    $[2] = delayDuration;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== defaultOpen) {
    t2 = defaultOpen !== undefined ? {
      defaultOpen
    } : {};
    $[4] = defaultOpen;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== open) {
    t3 = open !== undefined ? {
      open
    } : {};
    $[6] = open;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== onOpenChange) {
    t4 = onOpenChange !== undefined ? {
      onOpenChange
    } : {};
    $[8] = onOpenChange;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== children) {
    t5 = <TooltipTrigger asChild={true}>{children}</TooltipTrigger>;
    $[10] = children;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== side) {
    t6 = side !== undefined ? {
      side
    } : {};
    $[12] = side;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== align) {
    t7 = align !== undefined ? {
      align
    } : {};
    $[14] = align;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== sideOffset) {
    t8 = sideOffset !== undefined ? {
      sideOffset
    } : {};
    $[16] = sideOffset;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== contentClassName) {
    t9 = contentClassName !== undefined ? {
      className: contentClassName
    } : {};
    $[18] = contentClassName;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== contentStyle) {
    t10 = contentStyle !== undefined ? {
      style: contentStyle
    } : {};
    $[20] = contentStyle;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== arrowClassName) {
    t11 = arrowClassName !== undefined ? {
      arrowClassName
    } : {};
    $[22] = arrowClassName;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== showArrow) {
    t12 = showArrow !== undefined ? {
      showArrow
    } : {};
    $[24] = showArrow;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== content || $[27] !== t10 || $[28] !== t11 || $[29] !== t12 || $[30] !== t6 || $[31] !== t7 || $[32] !== t8 || $[33] !== t9) {
    t13 = <TooltipContent {...t6} {...t7} {...t8} {...t9} {...t10} {...t11} {...t12}>{content}</TooltipContent>;
    $[26] = content;
    $[27] = t10;
    $[28] = t11;
    $[29] = t12;
    $[30] = t6;
    $[31] = t7;
    $[32] = t8;
    $[33] = t9;
    $[34] = t13;
  } else {
    t13 = $[34];
  }
  let t14;
  if ($[35] !== t13 || $[36] !== t2 || $[37] !== t3 || $[38] !== t4 || $[39] !== t5) {
    t14 = <Tooltip {...t2} {...t3} {...t4}>{t5}{t13}</Tooltip>;
    $[35] = t13;
    $[36] = t2;
    $[37] = t3;
    $[38] = t4;
    $[39] = t5;
    $[40] = t14;
  } else {
    t14 = $[40];
  }
  let t15;
  if ($[41] !== t1 || $[42] !== t14) {
    t15 = <TooltipProvider {...t1}>{t14}</TooltipProvider>;
    $[41] = t1;
    $[42] = t14;
    $[43] = t15;
  } else {
    t15 = $[43];
  }
  return t15;
}
export const AppTooltip: AppTooltipComponent = Object.assign(AppTooltipImpl, {
  Provider: TooltipProvider
});